import type { EmotionJSX } from '@emotion/react/types/jsx-namespace';
import styled from '@emotion/styled';
import {
  Icon,
  ImageComponent,
  InnerHtml,
  Tooltip,
  Typography,
} from '@innovamat/glimmer-components';
import { useTranslation } from 'react-i18next';

const HoverOverlay = styled.div`
  align-items: flex-start;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  inset: 0;
  justify-content: space-between;
  opacity: 0;
  padding: 9px 9px 14px 9px;
  position: absolute;
  transition: opacity 0.3s ease-in-out;
  visibility: hidden;

  h5 {
    font-size: 15px;
    line-height: 17px;
    letter-spacing: 0.12px;
  }
`;

const DisabledOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.3);
`;

const RevisitMessage = styled.div`
  width: fit-content;
  display: flex;
  padding: 2px 4px;
  align-items: flex-start;
  border-radius: 4px;
  background: ${({ theme }) =>
    theme.tokens.color.alias.cm.surface['surface-secondary'].value};
  color: ${({ theme }) => theme.colors.black};
`;

const StyledSubtitle1 = styled(Typography.Subtitle1)`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 4;
  overflow: hidden;
  overflow-wrap: break-word;
  text-overflow: ellipsis;
`;

const Container = styled.div<{ isRevisited: boolean }>`
  align-items: flex-start;
  color: ${({ theme }) => theme.colors.white};
  display: flex;
  flex-direction: column;
  gap: 24px;
  justify-content: flex-end;
  overflow: hidden;
  position: relative;
  transition: filter 0.3s;
  width: 100%;
  border-radius: 4px;
  border: ${({ isRevisited, theme }) =>
    isRevisited
      ? `1px dashed ${theme.tokens.color.alias.cm.border['border-subtle'].value}`
      : '1px solid transparent'};
  cursor: pointer;

  &:hover .hover-overlay {
    opacity: 1;
    visibility: visible;
  }

  &:hover .icon-container {
    opacity: 1;
  }
`;

const IconContainer = styled.div`
  display: flex;
  height: 24px;
  justify-content: flex-end;
  align-items: flex-end;
  gap: 8px;
  opacity: 0;
  transition: opacity 0.3s;
`;

const Thumbnail = styled(ImageComponent)`
  aspect-ratio: 12/7;
  border-radius: 4px;
  object-fit: cover;
  object-position: bottom;
  width: 100%;
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  width: 100%;
`;

const IncludedExcluded = styled.span`
  display: block;
  bottom: 13px;
  color: ${({ theme }) => theme.colors.white};
  font-size: 12px;
  font-weight: 500;
  left: 60px;
  position: absolute;
`;

const LinkIconContainer = styled.div`
  position: absolute;
  bottom: 45px;
  right: 10px;
`;

type Props = {
  enabled?: boolean;
  isRevisited?: boolean;
  thumbnail: string;
  title: string;
  onClick: () => void;
};

function AppletCard({
  enabled = true,
  isRevisited = false,
  thumbnail,
  title,
  onClick,
}: Props): EmotionJSX.Element {
  const { t } = useTranslation();

  return (
    <Container isRevisited={isRevisited} onClick={onClick}>
      <Thumbnail className="thumbnail" src={thumbnail || ''} alt={title} />
      {!enabled && <DisabledOverlay />}
      <HoverOverlay className="hover-overlay">
        <TitleWrapper>
          {isRevisited && (
            <Tooltip
              content={t('homeWeeklyPractice.drawerRevisiatApplet.tooltip')}
            >
              <RevisitMessage>
                <Typography.Body3>
                  {t('homeWeeklyPractice.drawerRevisiatApplet.tag')}
                </Typography.Body3>
              </RevisitMessage>
            </Tooltip>
          )}
          <StyledSubtitle1>
            <InnerHtml text={title} />
          </StyledSubtitle1>
          <LinkIconContainer>
            <IconContainer className="icon-container">
              <Icon icon="OpenNewTabIcon" iconColor="white" size="S" />
            </IconContainer>
          </LinkIconContainer>
        </TitleWrapper>
        <IncludedExcluded>
          {enabled
            ? t('homeWeeklyPractice.activityIncluded')
            : t('homeWeeklyPractice.activityExcluded')}
        </IncludedExcluded>
      </HoverOverlay>
    </Container>
  );
}

export { AppletCard };
